import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSettings,
  updateSettings,
  uploadAvatar,
  type UserSettings,
  type UpdateSettingsData
} from '../api/settings';

/**
 * Hook to fetch and manage user settings
 * Uses TanStack Query for caching and state management
 */
export function useSettings() {
  const queryClient = useQueryClient();

  // Fetch settings query
  const {
    data: settings,
    isPending: isLoading,
    error,
    refetch,
  } = useQuery<UserSettings, Error>({
    queryKey: ['settings'],
    queryFn: getSettings,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep data in cache for 10 minutes (gcTime in v5)
    gcTime: 10 * 60 * 1000,
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation<UserSettings, Error, UpdateSettingsData>({
    mutationFn: updateSettings,
    onSuccess: (data) => {
      // Update the cache with new settings
      queryClient.setQueryData(['settings'], data);
    },
  });

  // Upload avatar mutation
  const uploadAvatarMutation = useMutation<{ avatar_url: string }, Error, File>({
    mutationFn: uploadAvatar,
    onSuccess: () => {
      // Refetch settings to get updated avatar_url
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  return {
    // Data
    settings,

    // Loading states
    isLoading,
    isUpdating: updateSettingsMutation.isPending,
    isUploadingAvatar: uploadAvatarMutation.isPending,

    // Errors
    error,
    updateError: updateSettingsMutation.error,
    uploadError: uploadAvatarMutation.error,

    // Actions
    updateSettings: updateSettingsMutation.mutate,
    uploadAvatar: uploadAvatarMutation.mutate,
    refetch,

    // Success states
    isUpdateSuccess: updateSettingsMutation.isSuccess,
    isUploadSuccess: uploadAvatarMutation.isSuccess,
  };
}
